<template>
<div class="pd-root" :style="themePageStyle">

  <nav class="pd-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.push('/shop')"><i class="mdi mdi-arrow-left" style="color:var(--fm-m,#A09888);font-size:22px;"></i></a>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="assets/images/flockr_logo.png" alt="Flockr" style="height:30px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Agro-Input Shop</span>
    </div>
    <button class="btn btn-sm btn-warning" @click="$router.push('/cart')"><i class="mdi mdi-cart me-1"></i>Cart</button>
  </div></nav>

  <div class="container pd-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <div v-else-if="!product" class="text-center py-5">
      <i class="mdi mdi-package-variant-closed-remove" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Product not found</h5>
      <p style="color:var(--fm-s,#6B6356);">{{ error || 'It may have been removed or paused.' }}</p>
      <button class="btn btn-warning" @click="$router.push('/shop')">Browse all products</button>
    </div>

    <div v-else class="row g-4">
      <!-- Photos -->
      <div class="col-md-6">
        <div class="pd-main-photo" :style="mainPhoto ? 'background-image:url('+imgUrl(mainPhoto)+')' : ''">
          <i v-if="!mainPhoto" class="mdi mdi-package-variant" style="font-size:80px;color:var(--fm-s,#3A3530);"></i>
        </div>
        <div v-if="photos.length > 1" class="pd-thumbs">
          <div v-for="(p, pi) in photos" :key="pi" class="pd-thumb"
               :class="{active: pi === activePhoto}"
               :style="'background-image:url('+imgUrl(p.photo_url || p)+')'"
               @click="activePhoto = pi"></div>
        </div>
      </div>

      <!-- Info + actions -->
      <div class="col-md-6">
        <span v-if="product.category_name" class="pd-cat">{{ product.category_name }}</span>
        <h2 class="pd-name">{{ product.name }}</h2>
        <div v-if="product.brand || product.manufacturer" style="color:var(--fm-m,#A09888);font-size:13px;margin-bottom:8px;">
          <i class="mdi mdi-tag-outline me-1"></i>{{ product.brand || product.manufacturer }}
        </div>

        <div class="pd-price-row">
          <span class="pd-price">KES {{ fNum(product.price) }}</span>
          <span v-if="product.price_unit" class="pd-unit">/{{ product.price_unit }}</span>
          <span v-if="product.compare_at_price && Number(product.compare_at_price) > Number(product.price)" class="pd-strike">KES {{ fNum(product.compare_at_price) }}</span>
        </div>

        <!-- Stock -->
        <div class="pd-stock">
          <span v-if="Number(product.stock_quantity||0) <= 0 && Number(product.allow_backorder||0) === 0" class="pd-oos">
            <i class="mdi mdi-close-circle me-1"></i>Out of stock
          </span>
          <span v-else-if="Number(product.stock_quantity||0) > 0 && Number(product.stock_quantity||0) <= Number(product.low_stock_threshold||5)" class="pd-low">
            <i class="mdi mdi-alert me-1"></i>Only {{ product.stock_quantity }} left
          </span>
          <span v-else class="pd-instock">
            <i class="mdi mdi-check-circle me-1"></i>{{ Number(product.allow_backorder||0) === 1 ? 'Available (backorder OK)' : 'In stock' }}
          </span>
        </div>

        <!-- Short description -->
        <p v-if="product.short_description" class="pd-short">{{ product.short_description }}</p>

        <!-- Quantity + Add to Cart -->
        <div class="pd-actions" v-if="canBuy">
          <div class="pd-qty">
            <button @click="qty = Math.max(1, qty - 1)" :disabled="qty <= 1"><i class="mdi mdi-minus"></i></button>
            <span>{{ qty }}</span>
            <button @click="qty = Math.min(maxQty, qty + 1)" :disabled="maxQty && qty >= maxQty"><i class="mdi mdi-plus"></i></button>
          </div>
          <button class="btn btn-warning text-dark fw-bold flex-fill" @click="addToCart" :disabled="addingToCart">
            <span v-if="addingToCart" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else :class="addedToCart ? 'mdi mdi-cart-check' : 'mdi mdi-cart-plus'" class="me-1"></i>
            {{ addedToCart ? 'Added — view cart' : 'Add to Cart — KES ' + fNum(Number(product.price) * qty) }}
          </button>
        </div>

        <!-- Prescription warning -->
        <div v-if="String(product.is_prescription_required) === '1'" class="pd-rx">
          <i class="mdi mdi-prescription me-1"></i>
          <strong>Prescription required.</strong> A licensed vet must approve this purchase before delivery.
        </div>

        <!-- Seller -->
        <div v-if="product.seller_name" class="pd-seller">
          <div style="color:var(--fm-s,#6B6356);font-size:11px;text-transform:uppercase;letter-spacing:.4px;">Sold by</div>
          <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:14px;margin-top:2px;">
            <i class="mdi mdi-storefront me-1" style="color:#BFA164;"></i>{{ product.seller_name }}
          </div>
        </div>
      </div>

      <!-- Description / specs -->
      <div class="col-12">
        <div class="pd-section">
          <h6 style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-text-box-outline me-1" style="color:#BFA164;"></i>Description</h6>
          <p style="color:var(--fm-m,#A09888);font-size:14px;line-height:1.7;white-space:pre-wrap;">{{ product.description }}</p>
        </div>

        <div v-if="product.ingredients" class="pd-section">
          <h6 style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-flask-outline me-1" style="color:#BFA164;"></i>Ingredients</h6>
          <p style="color:var(--fm-m,#A09888);font-size:13px;line-height:1.6;">{{ product.ingredients }}</p>
        </div>

        <div v-if="product.dosage_instructions" class="pd-section">
          <h6 style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-needle me-1" style="color:#BFA164;"></i>Dosage instructions</h6>
          <p style="color:var(--fm-m,#A09888);font-size:13px;line-height:1.6;">{{ product.dosage_instructions }}</p>
        </div>

        <div v-if="parsedSpecs.length" class="pd-section">
          <h6 style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-clipboard-text-outline me-1" style="color:#BFA164;"></i>Specifications</h6>
          <table class="pd-spec-table">
            <tr v-for="(spec, si) in parsedSpecs" :key="si">
              <td>{{ spec.k }}</td><td>{{ spec.v }}</td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important}
html[data-theme="light"] .pd-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .pd-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .pd-section,
html[data-theme="light"] .pd-seller{background:#FFFFFF!important;border-color:#E8E2DA!important}
</style>

<style scoped>
.pd-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.pd-topbar{position:sticky;top:0;z-index:100;padding:10px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.pd-container{padding-top:80px;padding-bottom:60px;max-width:1100px}

.pd-main-photo{width:100%;aspect-ratio:1;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;background-size:cover;background-position:center;display:flex;align-items:center;justify-content:center}
.pd-thumbs{display:flex;gap:6px;margin-top:10px;overflow-x:auto;padding-bottom:6px}
.pd-thumb{width:64px;height:64px;border-radius:8px;background-size:cover;background-position:center;border:2px solid var(--fm-br,#1E1E1E);cursor:pointer;flex-shrink:0;transition:border-color .15s}
.pd-thumb.active{border-color:#BFA164}

.pd-cat{display:inline-block;padding:3px 10px;border-radius:6px;background:rgba(191,161,100,.12);color:#BFA164;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px}
.pd-name{color:var(--fm-h,#F5F0E8);font-weight:700;line-height:1.3;margin-bottom:4px}
.pd-price-row{display:flex;align-items:baseline;gap:8px;margin:14px 0 8px}
.pd-price{color:#BFA164;font-weight:700;font-size:30px}
.pd-unit{color:var(--fm-s,#6B6356);font-size:14px}
.pd-strike{color:var(--fm-s,#6B6356);font-size:14px;text-decoration:line-through;margin-left:4px}

.pd-stock{margin-bottom:14px;font-size:12px}
.pd-oos{color:#FF6B6B}
.pd-low{color:#FF9800}
.pd-instock{color:#50C878}
.pd-short{color:var(--fm-m,#A09888);font-size:14px;line-height:1.6;margin-bottom:18px}

.pd-actions{display:flex;gap:10px;margin-bottom:14px}
.pd-qty{display:flex;align-items:center;gap:6px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;padding:4px}
.pd-qty button{width:36px;height:36px;background:transparent;border:none;color:#BFA164;cursor:pointer;display:flex;align-items:center;justify-content:center;border-radius:6px;font-size:16px}
.pd-qty button:hover:not(:disabled){background:rgba(191,161,100,.12)}
.pd-qty button:disabled{opacity:.3;cursor:not-allowed}
.pd-qty span{min-width:36px;text-align:center;color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px}

.pd-rx{margin-bottom:14px;padding:10px 14px;background:rgba(255,152,0,.08);border:1px solid rgba(255,152,0,.25);border-radius:8px;color:#FF9800;font-size:12px}
.pd-seller{padding:14px 16px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;margin-top:8px}

.pd-section{margin-top:24px;padding:18px;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px}
.pd-spec-table{width:100%;font-size:13px}
.pd-spec-table td{padding:6px 10px;border-bottom:1px solid var(--fm-br,#1E1E1E)}
.pd-spec-table td:first-child{color:var(--fm-s,#6B6356);text-transform:capitalize;width:40%}
.pd-spec-table td:last-child{color:var(--fm-h,#F5F0E8);font-weight:500}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "ProductDetail",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null, loading:true, error:'',
    product:null, qty:1, activePhoto:0,
    addingToCart:false, addedToCart:false
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    photos(){
      if (!this.product) return [];
      var arr = this.product.photos || [];
      if (!Array.isArray(arr) || !arr.length) {
        if (this.product.primary_image_url) return [{ photo_url: this.product.primary_image_url }];
        return [];
      }
      return arr;
    },
    mainPhoto(){
      if (!this.photos.length) return '';
      var p = this.photos[Math.min(this.activePhoto, this.photos.length - 1)];
      return p.photo_url || p;
    },
    canBuy(){
      if (!this.product) return false;
      if (Number(this.product.stock_quantity||0) <= 0 && Number(this.product.allow_backorder||0) === 0) return false;
      return this.product.status === 'active' || !this.product.status;
    },
    maxQty(){
      if (!this.product) return 0;
      return Number(this.product.allow_backorder||0) === 1 ? 0 : Number(this.product.stock_quantity || 0);
    },
    parsedSpecs(){
      if (!this.product || !this.product.specifications) return [];
      try {
        var s = typeof this.product.specifications === 'string' ? JSON.parse(this.product.specifications) : this.product.specifications;
        if (!s || typeof s !== 'object') return [];
        return Object.keys(s).map(function(k){ return { k: String(k).replace(/_/g, ' '), v: String(s[k]) }; });
      } catch(e){ return []; }
    }
  },
  watch: {
    '$route.params.idOrSlug'(){this.load()}
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.load();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    fNum(n){return Number(n||0).toLocaleString()},
    imgUrl(u){if(!u)return'';if(u.indexOf('http')===0)return u;return uploads+String(u).replace(/^uploads\//,'');},
    async load(){
      this.loading=true; this.error=''; this.product=null; this.activePhoto=0; this.qty=1; this.addedToCart=false;
      var idOrSlug=this.$route.params.idOrSlug;
      var params={function:'getProduct'};
      // UUIDs are strings — try both fields, server will use whichever is set
      if (/^[0-9a-f-]{36}$/i.test(idOrSlug)) params.productId = idOrSlug;
      else params.slug = idOrSlug;
      try{
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success&&r.data.data&&r.data.data.product){
          this.product=r.data.data.product;
        }else{
          this.error=(r.data&&r.data.message)||'Product not found';
        }
      }catch(e){this.error=(e&&e.message)||'Network error'}
      this.loading=false;
    },
    async addToCart(){
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login?redirect=/shop/product/' + this.$route.params.idOrSlug); return; }
      if (this.addedToCart) { this.$router.push('/cart'); return; }
      this.addingToCart = true;
      try {
        var r = await executeJson({
          function:'addToCart',
          userId: userId,
          productId: this.product.id,
          quantity: this.qty
        }, flocker);
        if (r.data && r.data.success) {
          this.addedToCart = true;
          // Bump the badge
          var newCount = (Number(sessionStorage.getItem('flockr_cart_count') || 0)) + this.qty;
          sessionStorage.setItem('flockr_cart_count', newCount);
          window.dispatchEvent(new CustomEvent('flockr-cart-updated', { detail: { count: newCount } }));
        } else {
          alert((r.data && r.data.message) || 'Failed to add to cart');
        }
      } catch(e){ alert('Network error'); }
      this.addingToCart = false;
    }
  }
};
</script>
